import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  FileText,
  HelpCircle,
  Home,
  Menu,
  MessageSquare,
  Percent,
  EyeOff,
  ShoppingCart,
  X,
  Plus,
  Store,
  SlidersHorizontal,
  Monitor,
  Mail,
  Share2,
  Building2,
  Users,
  Group,
} from 'lucide-react';
import { Badge, Button, Form } from 'react-bootstrap';
import { ROUTE } from '../utils/navigation';
import { isQuoteChatUiEnabled, isQuoteDistributionUiEnabled } from '../utils/featureFlags';
import '../components/MainApp.css';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
];

const BUSINESS_LINKS = [
  { path: ROUTE.COMPANIES, labelKey: 'nav.companies', icon: Building2 },
  { path: ROUTE.CUSTOMERS, labelKey: 'nav.customers', icon: Users },
  { path: ROUTE.CUSTOMER_GROUPS, labelKey: 'nav.customerGroups', icon: Group },
];

const SETUP_LINKS = [
  { path: ROUTE.SETTINGS_STORE_PROFILE, labelKey: 'nav.storeProfile', icon: Store },
  { path: ROUTE.SETTINGS_BASIC_SETUP, labelKey: 'nav.basicSetup', icon: SlidersHorizontal },
  { path: ROUTE.SETTINGS_CUSTOMER_INTERFACE, labelKey: 'nav.customerInterface', icon: Monitor },
  { path: ROUTE.SETTINGS_EMAIL, labelKey: 'nav.email', icon: Mail },
  {
    path: ROUTE.SETTINGS_DISTRIBUTION,
    labelKey: 'nav.distribution',
    icon: Share2,
    hidden: !isQuoteDistributionUiEnabled(),
  },
];

const TOOLS_LINKS = [
  { path: ROUTE.TOOLS_DISCOUNTS, labelKey: 'nav.automaticDiscounts', icon: Percent },
  { path: ROUTE.TOOLS_HIDE_PRODUCTS, labelKey: 'nav.hideAddToQuote', icon: EyeOff },
  { path: ROUTE.TOOLS_HIDE_ADD_TO_CART, labelKey: 'nav.hideAddToCart', icon: ShoppingCart },
];

const isRouteActive = (route, path) => route?.path === path || route?.path?.startsWith(`${path}/`);

const isQuotesActive = (route) => route?.name?.startsWith('quotes');

const AppShell = ({ storeInfo, settings, children, route, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const topbarRef = useRef(null);

  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof document === 'undefined') return undefined;
    const publishOffset = () => {
      const h = Math.ceil(el.getBoundingClientRect().height) || 56;
      document.documentElement.style.setProperty('--q2c-app-topbar-offset', `${h}px`);
    };
    publishOffset();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishOffset) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', publishOffset);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', publishOffset);
    };
  }, []);

  const storeName =
    storeInfo?.generalInfo?.storeName ||
    storeInfo?.name ||
    settings?.storeName ||
    settings?.companyName ||
    'Your store';
  const trialDays = Number(settings?.trialDaysRemaining);
  const subscriptionStatus = settings?.subscriptionStatus || 'trial';

  const trialLabel = useMemo(() => {
    if (subscriptionStatus === 'active') return t('topbar.subscribed');
    if (subscriptionStatus === 'expired') return t('topbar.trialExpired');
    const days = Number.isFinite(trialDays) ? Math.max(0, trialDays) : null;
    if (days == null) return t('topbar.trial');
    return t('topbar.trialRemaining', { days });
  }, [subscriptionStatus, trialDays, t]);

  const closeMobileNav = useCallback(() => {
    if (window.innerWidth < 768) setSidebarOpen(false);
  }, []);

  const nav = useCallback(
    (path) => {
      onNavigate(path);
      closeMobileNav();
    },
    [onNavigate, closeMobileNav],
  );

  const navBtnClass = (active) => `sidebar-nav-item${active ? ' active' : ''}`;

  const currentLang =
    LANGUAGES.find((l) => i18n.language === l.code || String(i18n.language || '').startsWith(l.code))
      ?.code || 'en';

  return (
    <div className="main-app" data-main-layout="true" data-active-route={route?.path}>
      <div className="container-fluid p-0 d-flex flex-column main-app-shell">
        <header
          ref={topbarRef}
          className="app-topbar d-flex align-items-center justify-content-between px-3 py-2 bg-white border-bottom"
        >
          <div className="d-flex align-items-center gap-3">
            <img
              src={`${import.meta.env.BASE_URL}quote2cart_long_logo.png`}
              alt="Quote2Cart"
              className="app-topbar-logo"
            />
            <Badge
              bg={
                subscriptionStatus === 'active'
                  ? 'success'
                  : subscriptionStatus === 'expired'
                    ? 'danger'
                    : 'warning'
              }
              text={subscriptionStatus === 'active' ? 'white' : 'dark'}
            >
              {trialLabel}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              className="app-topbar-lang"
              aria-label={t('topbar.language')}
              value={currentLang}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
            >
              {LANGUAGES.map(({ code, label }) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </Form.Select>
            <Button
              size="sm"
              variant="primary"
              className="app-topbar-create d-flex align-items-center gap-1"
              onClick={() => nav(ROUTE.QUOTES_CREATE)}
            >
              <Plus size={16} />
              {t('topbar.createQuote')}
            </Button>
          </div>
        </header>

        <div className="app-body">
        <div className="row g-0 m-0 app-body-row">
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={t('mainApp.toggleMenu')}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} role="presentation" />}

          <div className={`col-md-3 col-lg-2 sidebar d-flex flex-column ${sidebarOpen ? 'sidebar-open' : ''}`}>
            {/* <div className="sidebar-header">
              <p className="sidebar-store-name">{storeName}</p>
            </div> */}

            <nav className="sidebar-nav flex-grow-1">
              <div className="sidebar-section-title">{t('nav.workspace')}</div>
              <button type="button" className={navBtnClass(route?.name === 'dashboard')} onClick={() => nav(ROUTE.DASHBOARD)}>
                <Home size={16} className="me-2" />
                {t('nav.dashboard')}
              </button>
              <button type="button" className={navBtnClass(isQuotesActive(route))} onClick={() => nav(ROUTE.QUOTES)}>
                <FileText size={16} className="me-2" />
                {t('nav.quotes')}
              </button>
              {isQuoteChatUiEnabled() ? (
                <button type="button" className={navBtnClass(route?.name === 'chat')} onClick={() => nav(ROUTE.CHAT)}>
                  <MessageSquare size={16} className="me-2" />
                  {t('nav.quoteChat')}
                </button>
              ) : null}

              <div className="sidebar-section-title">{t('nav.business')}</div>
              {BUSINESS_LINKS.map((link) => {
                const Icon = link.icon;
                const active =
                  link.path === ROUTE.CUSTOMERS
                    ? route?.name === 'customers' || route?.name === 'customers-detail'
                    : isRouteActive(route, link.path) || (link.path === ROUTE.CUSTOMER_GROUPS && route?.name === 'customer-groups');
                return (
                  <button
                    key={link.path}
                    type="button"
                    className={navBtnClass(active)}
                    onClick={() => nav(link.path)}
                  >
                    <Icon size={16} className="me-2" />
                    {t(link.labelKey)}
                  </button>
                );
              })}

              <div className="sidebar-section-title">{t('nav.setup')}</div>
              {SETUP_LINKS.filter((link) => !link.hidden).map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.path}
                    type="button"
                    className={navBtnClass(isRouteActive(route, link.path))}
                    onClick={() => nav(link.path)}
                  >
                    <Icon size={16} className="me-2" />
                    {t(link.labelKey)}
                  </button>
                );
              })}

              <div className="sidebar-section-title">{t('nav.tools')}</div>
              {TOOLS_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.path}
                    type="button"
                    className={navBtnClass(isRouteActive(route, link.path))}
                    onClick={() => nav(link.path)}
                  >
                    <Icon size={16} className="me-2" />
                    {t(link.labelKey)}
                  </button>
                );
              })}

              <div className="sidebar-section-title">{t('nav.account')}</div>
              <button type="button" className={navBtnClass(isRouteActive(route, ROUTE.ACCOUNT_PAYMENTS))} onClick={() => nav(ROUTE.ACCOUNT_PAYMENTS)}>
                <CreditCard size={16} className="me-2" />
                {t('nav.payments')}
              </button>
              <button type="button" className={navBtnClass(route?.name === 'help')} onClick={() => nav(ROUTE.HELP)}>
                <HelpCircle size={16} className="me-2" />
                {t('nav.help')}
              </button>
            </nav>
          </div>

          <div className="col-md-9 col-lg-10 d-flex flex-column p-0">
            <div className="main-content-wrapper flex-grow-1">
              <main className="main-content">{children}</main>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
